// The handful of things that are CSS and not props.
//
// Tamagui covers the interface, but four of §15's requirements are web-platform
// features with no React Native equivalent and therefore no Tamagui prop:
// `backdrop-filter` (panels "translucent and blurred over the map"),
// `font-variant-numeric: tabular-nums` (counters that "do not jitter as they
// tick"), the scanline sweep, and the vignette. Rather than scatter `style`
// escapes through the components, they live here as four class names.
//
// Every value in this sheet comes from theme.ts, which comes from tokens.json.
// The string is built rather than written so that stays true.
//
// The reduced-motion rules are duplicated deliberately: once against the media
// query, so a player who never touches our controls gets it from the browser, and
// once against `[data-motion="reduced"]`, so our own switch works on a machine
// whose system preference says otherwise.

import { colour, fontFamily, motion } from './theme';

export const PanelClass = 'wwp-panel';
export const TabularClass = 'wwp-tabular';
export const ScanClass = 'wwp-scan';
export const VignetteClass = 'wwp-vignette';
export const RootClass = 'wwp-root';

export function globalCSS(): string {
  return `
:root { color-scheme: dark; }

html, body, #root {
  margin: 0;
  padding: 0;
  height: 100%;
  background: ${colour.base};
  color: ${colour.text};
  font-family: ${fontFamily.mono};
  overflow: hidden;
  overscroll-behavior: none;
}

/* User content is never dressed in the theme (§15). */
.wwp-user { font-family: ${fontFamily.sans}; letter-spacing: 0; }

/* Counters must not jitter as they tick. */
.${TabularClass} {
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1, 'zero' 1;
}

/* "Panels are translucent and blurred over the map, never opaque." */
.${PanelClass} {
  backdrop-filter: blur(10px) saturate(0.55);
  -webkit-backdrop-filter: blur(10px) saturate(0.55);
}

/* A slow scanline sweep across panels. */
.${ScanClass} { position: relative; overflow: hidden; }
.${ScanClass}::after {
  content: '';
  position: absolute;
  left: 0; right: 0; top: -40%;
  height: 40%;
  pointer-events: none;
  background: linear-gradient(180deg, transparent, ${colour.scanline}, transparent);
  animation: wwp-scan ${motion.scanMs}ms ${motion.easing} infinite;
}
@keyframes wwp-scan {
  from { transform: translateY(0); }
  to   { transform: translateY(350%); }
}

/* A subtle vignette over the whole canvas. */
.${VignetteClass}::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(ellipse at center, transparent 55%, ${colour.panelFillDeep} 100%);
}

/* Reduced motion (§15, not negotiable): the arcs stay, the decoration goes. */
@media (prefers-reduced-motion: reduce) {
  .${ScanClass}::after { display: none; }
  * { animation-duration: 0.001ms !important; transition-duration: 0.001ms !important; }
}
[data-motion='reduced'] .${ScanClass}::after { display: none; }
[data-motion='reduced'] * { animation-duration: 0.001ms !important; transition-duration: 0.001ms !important; }

/* The map canvas is the only bright thing on screen, and it owns the cursor. */
canvas.wwp-map { display: block; width: 100%; height: 100%; cursor: crosshair; touch-action: none; }

/*
  The three volume sliders and the reduced-motion switch.

  Native controls, restyled rather than replaced: they are the two places in the
  interface where keyboard operation and a screen-reader label have to work with
  no effort, and §15's accessibility clause does not bend for the aesthetic.
*/
.wwp-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 10px;
  background: transparent;
  cursor: pointer;
}
.wwp-slider::-webkit-slider-runnable-track {
  height: 2px;
  background: ${colour.hairline};
}
.wwp-slider::-moz-range-track { height: 2px; background: ${colour.hairline}; }
.wwp-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 3px;
  height: 12px;
  margin-top: -5px;
  border-radius: 0;
  background: ${colour.cyan};
}
.wwp-slider::-moz-range-thumb {
  width: 3px; height: 12px; border: 0; border-radius: 0; background: ${colour.cyan};
}
.wwp-slider:focus-visible { outline: 1px solid ${colour.bracket}; outline-offset: 2px; }

.wwp-check {
  appearance: none;
  width: 12px; height: 12px;
  border: 1px solid ${colour.hairline};
  background: ${colour.panelFillDeep};
  cursor: pointer;
}
.wwp-check:checked { background: ${colour.cyan}; }
.wwp-check:focus-visible { outline: 1px solid ${colour.bracket}; outline-offset: 2px; }
`;
}

/** Injects the sheet once. A second call is a no-op, which matters under HMR. */
export function installGlobalCSS(doc: Document): void {
  const id = 'wwp-global-css';
  if (doc.getElementById(id)) return;
  const el = doc.createElement('style');
  el.id = id;
  el.textContent = globalCSS();
  doc.head.appendChild(el);
}
